export interface TrendingVideo {
  videoId: string;
  videoUrl: string;
  videoPath: string;
  productFocus: string;
  productLink: string;
  productSlug: string;
  contentLink?: string;
  order: number;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTrendingVideoPayload {
  videoUrl: string;
  productFocus: string;
  productLink: string;
  contentLink?: string;
  order?: number;
  isVisible?: boolean;
}

export interface UpdateTrendingVideoPayload {
  productFocus?: string;
  productLink?: string;
  contentLink?: string;
  order?: number;
  isVisible?: boolean;
}

export interface ListTrendingVideosResponse {
  videos: TrendingVideo[];
  lastEvaluatedKey?: string;
}

interface TrendingVideoOrderResult {
  videoId: string;
  success: boolean;
  order?: number;
  error?: string;
}

interface BulkUpdateTrendingVideoOrderResponse {
  success: boolean;
  totalProcessed: number;
  successCount: number;
  failureCount: number;
  orderResults: TrendingVideoOrderResult[];
}

interface PresignedUrlResponse {
  presignedUrl: string;
  objectKey: string;
  cdnUrl: string;
}

const ADMIN_CACHE_BUST_PARAM = '_adminCacheBust';

export class TrendingVideoClient {
  private readonly writeHeaders: Record<string, string>;

  constructor(
    private readonly apiUrl: string,
    apiKey: string,
  ) {
    this.writeHeaders = {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    };
  }

  async getPresignedUrl(fileName: string, contentType: string, sizeBytes: number): Promise<PresignedUrlResponse> {
    const response = await fetch(`${this.apiUrl}/trendingVideos/presignedUrl`, {
      method: 'POST',
      headers: this.writeHeaders,
      body: JSON.stringify({ fileName, contentType, sizeBytes }),
    });
    if (!response.ok) {
      throw new Error(await this.readError(response, 'Failed to get presigned URL'));
    }
    return response.json();
  }

  async create(payload: CreateTrendingVideoPayload): Promise<TrendingVideo> {
    const response = await fetch(`${this.apiUrl}/trendingVideos`, {
      method: 'POST',
      headers: this.writeHeaders,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(await this.readError(response, 'Failed to create trending video'));
    }
    return response.json();
  }

  async list(params?: {
    visibleOnly?: boolean;
    limit?: number;
    lastEvaluatedKey?: string;
  }): Promise<ListTrendingVideosResponse> {
    const query = new URLSearchParams();
    if (params?.visibleOnly !== undefined) {
      query.append('visibleOnly', String(params.visibleOnly));
    }
    if (params?.limit) {
      query.append('limit', String(params.limit));
    }
    if (params?.lastEvaluatedKey) {
      query.append('lastEvaluatedKey', params.lastEvaluatedKey);
    }
    query.set(ADMIN_CACHE_BUST_PARAM, Date.now().toString());

    const response = await fetch(`${this.apiUrl}/trendingVideos?${query.toString()}`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(await this.readError(response, 'Failed to list trending videos'));
    }
    return response.json();
  }

  async update(videoId: string, patch: UpdateTrendingVideoPayload): Promise<TrendingVideo> {
    const response = await fetch(`${this.apiUrl}/trendingVideos/${videoId}`, {
      method: 'PUT',
      headers: this.writeHeaders,
      body: JSON.stringify(patch),
    });
    if (!response.ok) {
      throw new Error(await this.readError(response, 'Failed to update trending video'));
    }
    return response.json();
  }

  async delete(videoId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/trendingVideos/${videoId}`, {
      method: 'DELETE',
      headers: this.writeHeaders,
    });
    if (!response.ok) {
      throw new Error(await this.readError(response, 'Failed to delete trending video'));
    }
  }

  async updateOrders(videos: Array<{ videoId: string; order: number }>): Promise<void> {
    const response = await fetch(`${this.apiUrl}/trendingVideos/bulk/order`, {
      method: 'PUT',
      headers: this.writeHeaders,
      body: JSON.stringify({ videos }),
    });
    if (!response.ok) {
      throw new Error(await this.readError(response, 'Failed to update trending video orders'));
    }
    const result = (await response.json()) as BulkUpdateTrendingVideoOrderResponse;
    if (!result.success) {
      const failures = result.orderResults
        .filter((orderResult) => !orderResult.success)
        .map((orderResult) => `${orderResult.videoId}: ${orderResult.error ?? 'unknown error'}`);
      throw new Error(
        failures.length > 0
          ? `Failed to update trending video orders: ${failures.join(', ')}`
          : 'Failed to update trending video orders',
      );
    }
  }

  private async readError(response: Response, fallback: string): Promise<string> {
    try {
      const body = await response.json();
      const message = body?.message;
      if (Array.isArray(message)) {
        return message.join(', ');
      }
      if (typeof message === 'string') {
        return message;
      }
    } catch {
      // fall through
    }
    return `${fallback}: ${response.statusText}`;
  }
}
